// DM5 — one save model. The shared visual vocabulary every save surface speaks:
// the timeline transport's soundscape save AND the inspector's per-aspect
// buttons (manifest / camera / atmosphere). Before this each had its own
// feedback — the timeline was conflict-aware (saving/saved/failed/conflict, the
// conflict held on screen), the inspector only flashed ✓/✕ and couldn't show a
// disk conflict at all. One source of truth here so they can't drift.
//
// Colors are the dev palette (timeline.ts + inspector.ts share these exact
// constants); kept as literals so this module has no cross-import.

export type SaveVis = 'clean' | 'dirty' | 'saving' | 'saved' | 'failed' | 'conflict';

const TEXT = '#e6ebef';
const MUTED = '#6c7480';

export interface SaveVisSpec {
  /** button text */ t: string;
  /** text color */ c: string;
  /** border color */ b: string;
  /** tooltip (conflict carries the reload-or-force explainer) */ title?: string;
}

export const SAVE_VIS: Record<SaveVis, SaveVisSpec> = {
  clean:    { t: 'save',    c: MUTED, b: 'rgba(255,255,255,0.12)' },
  dirty:    { t: '● save',  c: '#ffd87c', b: '#ffd87c' }, // amber: unsaved edits
  saving:   { t: 'saving…', c: TEXT, b: 'rgba(255,255,255,0.30)' },
  saved:    { t: 'saved ✓', c: '#6acf91', b: '#6acf91' }, // green: on disk
  failed:   { t: 'save ✕',  c: '#ff8a8a', b: '#ff8a8a' }, // red: NOT on disk (network/server)
  // Distinct text + tooltip from `failed`: the file changed on disk since load,
  // so the write was refused rather than lost — the user must reload or force.
  // Held on screen (the caller does not auto-restore a conflict).
  conflict: { t: 'disk ✕',  c: '#ff8a8a', b: '#ff8a8a',
    title: 'changed on disk since you loaded it — reload to take the disk copy, or save again to overwrite it with your edits (force).' },
};

/** Paint a save state onto a button. `defaultTitle` is the surface's own
 *  resting tooltip (used for the non-conflict states). */
export function paintSave(btn: HTMLButtonElement, vis: SaveVis, defaultTitle?: string): void {
  const m = SAVE_VIS[vis];
  btn.textContent = m.t;
  btn.style.color = m.c;
  btn.style.borderColor = m.b;
  btn.title = m.title ?? defaultTitle ?? '';
}
